import {useCallback} from "react";
import {css} from "@panda/css";

import {Badge} from "@pg-design/badge-module";
import {CenteredImage} from "@pg-design/centered-image";
import {PercentIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {head} from "@pg-mono/nodash";
import {rpAppLink} from "@pg-mono/rp-routes";
import {areaFormat, priceFormat} from "@pg-mono/string-utils";
import {LISTING_VENDOR_TEST_IDS, PROPERTY_CARD_TEST_ID} from "@pg-mono/test-ids";

import {PropertyViewRedirectButton} from "../../../../atoms/PropertyBox/PropertyViewRedirectButton";
import {FavoriteButton} from "../../../../favorite/components/FavoriteButton";
import {useResponsiveLinkTarget} from "../../../../hooks/use_responsive_link_target";
import {useShouldShowPriceByOfferConfigAndUser} from "../../../../hooks/use_should_show_price_by_offer_config_and_user";
import {useOfferPageAmplitudeItemListTrackingContext} from "../../../../promotion/tracking/OfferPageAmplitudeItemListTrackingContext";
import {IPropertyListProperty} from "../../../../property/types/PropertyListProperty";
import {getPropertyPlanAltText} from "../../../../property/utils/get_property_plan_modal_title";
import {getCurrency} from "../../../../utils/get_currency";
import {getThumbnailLink} from "../../../../utils/get_thumbnail_link";
import {OfferType} from "../../../types/OfferType";

interface IProps {
    property: IPropertyListProperty;
    onClick?: (property: IPropertyListProperty) => void;
}

export const OfferDetailPromotionPropertyBox = (props: IProps) => {
    const {property, onClick} = props;
    const {offer} = property;

    const propertyLink = rpAppLink.property.detail.base({
        vendorSlug: offer.vendor.slug,
        offerSlug: offer.slug,
        offerId: offer.id,
        propertyId: property.id
    });

    const target = useResponsiveLinkTarget();
    const {pageLocation} = useOfferPageAmplitudeItemListTrackingContext();
    const firstPlanPage = head(property.plan_image_pages);
    const planImage = property.external_plan_url || getThumbnailLink(firstPlanPage?.image_url, 375, 250);
    const altText = getPropertyPlanAltText(offer.name, property);

    const shouldShowPrice = useShouldShowPriceByOfferConfigAndUser({
        offerPriceVisibility: offer.configuration.price_visibility,
        vendorPriceVisibility: offer.vendor.configuration.price_visibility,
        vendorId: offer.vendor.id
    });
    const showPrice = shouldShowPrice && !!property.price;

    const isHouse = offer.type === OfferType.HOUSE;

    const handleLinkClick = useCallback(() => {
        onClick?.(property);
    }, [onClick, property]);

    return (
        <div className={cardStyle}>
            <FavoriteButton
                offer={offer}
                property={property}
                display={{type: "icon"}}
                className={favoriteButtonStyle}
                dataTestId={LISTING_VENDOR_TEST_IDS.FAVOURITE_ICON}
                favouritePlacement="rp_favourite_promo_tile"
                favouriteLocation={pageLocation}
            />

            <a
                href={propertyLink}
                onClick={handleLinkClick}
                target={target}
                title={offer.name}
                className={linkStyle}
                data-testid={LISTING_VENDOR_TEST_IDS.PROPERTY_CONTAINER}
            >
                <Badge variant="label_info" className={promotionBadgeStyle} icon={() => <PercentIcon size="1.6" className={promotionBadgeIconStyle} />}>
                    <Text variant="info_txt_2" as="span" strong>
                        Promocja
                    </Text>
                </Badge>

                <div className={planWrapStyle} data-testid={LISTING_VENDOR_TEST_IDS.PROPERTY_CARD_IMAGE}>
                    <CenteredImage
                        src={planImage}
                        alt={altText}
                        width={375}
                        height={250}
                        breakpoints={[{mediaWidth: "0", height: "200px", width: "100%"}]}
                        loading="lazy"
                    />
                </div>

                <ul className={infoListStyle}>
                    <li className={infoItemStyle}>
                        <Text className={labelStyle} variant="info_txt_3" as="span">
                            Metraż
                        </Text>
                        <Text variant="info_txt_1" as="span" dataTestId={PROPERTY_CARD_TEST_ID.AREA}>
                            {areaFormat(property.area, {precision: 2})}
                        </Text>
                    </li>

                    <li className={infoItemStyle}>
                        <Text className={labelStyle} variant="info_txt_3" as="span">
                            Pokoje
                        </Text>
                        <Text variant="info_txt_1" as="span" dataTestId={PROPERTY_CARD_TEST_ID.ROOMS}>
                            {property.rooms}
                        </Text>
                    </li>

                    <li className={infoItemStyle}>
                        <Text className={labelStyle} variant="info_txt_3" as="span">
                            {isHouse ? "Piętra" : "Piętro"}
                        </Text>
                        <Text variant="info_txt_1" as="span" dataTestId={PROPERTY_CARD_TEST_ID.FLOOR}>
                            {isHouse ? property.floors : property.floor === 0 ? "parter" : property.floor}
                        </Text>
                    </li>

                    {showPrice && (
                        <li className={infoItemStyle}>
                            <Text className={labelStyle} variant="info_txt_3" as="span">
                                Cena
                            </Text>
                            <Text variant="info_txt_1" as="span" strong>
                                {priceFormat(property.price as number, {
                                    unit: getCurrency({currency: offer.currency})
                                })}
                            </Text>
                        </li>
                    )}
                </ul>

                <PropertyViewRedirectButton property={property} isPlansList />
            </a>
        </div>
    );
};

const cardStyle = css({
    position: "relative",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "100%",
    // match the regular property tile (OfferBoxBase) width so promotion tiles are the same size, incl. on mobile
    minWidth: "288px",
    maxWidth: "375px",
    p: 2,
    backgroundColor: "var(--colors-white)",
    borderRadius: 4,
    boxShadow: "var(--shadows-2)"
});

const favoriteButtonStyle = css({
    position: "absolute",
    top: 2,
    right: 2,
    zIndex: 30
});

const promotionBadgeStyle = css({
    position: "absolute",
    top: 2,
    left: 2,
    zIndex: 20
});

const promotionBadgeIconStyle = css({
    mr: 1
});

const linkStyle = css({
    display: "flex",
    flexDirection: "column",
    flex: "1 1 auto",
    cursor: "pointer",
    color: "inherit",
    _hover: {color: "inherit", textDecoration: "none"},
    _focus: {color: "inherit", textDecoration: "none"}
});

const planWrapStyle = css({
    flex: "0 0 auto",
    "& img": {
        objectFit: "contain",
        width: "100% !important",
        height: "100% !important"
    }
});

const infoListStyle = css({
    listStyle: "none",
    pl: 0,
    mt: 3,
    mb: 2,
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-evenly",
    alignItems: "center"
});

const infoItemStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center"
});

const labelStyle = css({
    color: "var(--colors-gray-700)",
    mb: 0.5
});
import {useCallback} from "react";
import {css} from "@panda/css";

import {Badge} from "@pg-design/badge-module";
import {CenteredImage} from "@pg-design/centered-image";
import {PercentIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {head} from "@pg-mono/nodash";
import {rpAppLink} from "@pg-mono/rp-routes";
import {areaFormat, priceFormat} from "@pg-mono/string-utils";
import {LISTING_VENDOR_TEST_IDS, PROPERTY_CARD_TEST_ID} from "@pg-mono/test-ids";

import {PropertyViewRedirectButton} from "../../../../atoms/PropertyBox/PropertyViewRedirectButton";
import {FavoriteButton} from "../../../../favorite/components/FavoriteButton";
import {useResponsiveLinkTarget} from "../../../../hooks/use_responsive_link_target";
import {useShouldShowPriceByOfferConfigAndUser} from "../../../../hooks/use_should_show_price_by_offer_config_and_user";
import {useOfferPageAmplitudeItemListTrackingContext} from "../../../../promotion/tracking/OfferPageAmplitudeItemListTrackingContext";
import {IPropertyListProperty} from "../../../../property/types/PropertyListProperty";
import {getPropertyPlanAltText} from "../../../../property/utils/get_property_plan_modal_title";
import {getCurrency} from "../../../../utils/get_currency";
import {getThumbnailLink} from "../../../../utils/get_thumbnail_link";
import {OfferType} from "../../../types/OfferType";

interface IProps {
    property: IPropertyListProperty;
    onClick?: (property: IPropertyListProperty) => void;
}

export const OfferDetailPromotionPropertyBox = (props: IProps) => {
    const {property, onClick} = props;
    const {offer} = property;

    const propertyLink = rpAppLink.property.detail.base({
        vendorSlug: offer.vendor.slug,
        offerSlug: offer.slug,
        offerId: offer.id,
        propertyId: property.id
    });

    const target = useResponsiveLinkTarget();
    const {pageLocation} = useOfferPageAmplitudeItemListTrackingContext();
    const firstPlanPage = head(property.plan_image_pages);
    const planImage = property.external_plan_url || getThumbnailLink(firstPlanPage?.image_url, 375, 250);
    const altText = getPropertyPlanAltText(offer.name, property);

    const shouldShowPrice = useShouldShowPriceByOfferConfigAndUser({
        offerPriceVisibility: offer.configuration.price_visibility,
        vendorPriceVisibility: offer.vendor.configuration.price_visibility,
        vendorId: offer.vendor.id
    });
    const showPrice = shouldShowPrice && !!property.price;

    const isHouse = offer.type === OfferType.HOUSE;

    const handleLinkClick = useCallback(() => {
        onClick?.(property);
    }, [onClick, property]);

    return (
        <div className={cardStyle}>
            <FavoriteButton
                offer={offer}
                property={property}
                display={{type: "icon"}}
                className={favoriteButtonStyle}
                dataTestId={LISTING_VENDOR_TEST_IDS.FAVOURITE_ICON}
                favouritePlacement="rp_favourite_promo_tile"
                favouriteLocation={pageLocation}
            />

            <a
                href={propertyLink}
                onClick={handleLinkClick}
                target={target}
                title={offer.name}
                className={linkStyle}
                data-testid={LISTING_VENDOR_TEST_IDS.PROPERTY_CONTAINER}
            >
                <Badge variant="label_info" className={promotionBadgeStyle} icon={() => <PercentIcon size="1.6" className={promotionBadgeIconStyle} />}>
                    <Text variant="info_txt_2" as="span" strong>
                        Promocja
                    </Text>
                </Badge>

                <div className={planWrapStyle} data-testid={LISTING_VENDOR_TEST_IDS.PROPERTY_CARD_IMAGE}>
                    <CenteredImage
                        src={planImage}
                        alt={altText}
                        width={375}
                        height={250}
                        breakpoints={[{mediaWidth: "0", height: "200px", width: "100%"}]}
                        loading="lazy"
                    />
                </div>

                <ul className={infoListStyle}>
                    <li className={infoItemStyle}>
                        <Text className={labelStyle} variant="info_txt_3" as="span">
                            Metraż
                        </Text>
                        <Text variant="info_txt_1" as="span" dataTestId={PROPERTY_CARD_TEST_ID.AREA}>
                            {areaFormat(property.area, {precision: 2})}
                        </Text>
                    </li>

                    <li className={infoItemStyle}>
                        <Text className={labelStyle} variant="info_txt_3" as="span">
                            Pokoje
                        </Text>
                        <Text variant="info_txt_1" as="span" dataTestId={PROPERTY_CARD_TEST_ID.ROOMS}>
                            {property.rooms}
                        </Text>
                    </li>

                    <li className={infoItemStyle}>
                        <Text className={labelStyle} variant="info_txt_3" as="span">
                            {isHouse ? "Piętra" : "Piętro"}
                        </Text>
                        <Text variant="info_txt_1" as="span" dataTestId={PROPERTY_CARD_TEST_ID.FLOOR}>
                            {isHouse ? property.floors : property.floor === 0 ? "parter" : property.floor}
                        </Text>
                    </li>

                    {showPrice && (
                        <li className={infoItemStyle}>
                            <Text className={labelStyle} variant="info_txt_3" as="span">
                                Cena
                            </Text>
                            <Text variant="info_txt_1" as="span" strong>
                                {priceFormat(property.price as number, {
                                    unit: getCurrency({currency: offer.currency})
                                })}
                            </Text>
                        </li>
                    )}
                </ul>

                <PropertyViewRedirectButton property={property} isPlansList />
            </a>
        </div>
    );
};

const cardStyle = css({
    position: "relative",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "100%",
    // match the regular property tile (OfferBoxBase) width so promotion tiles are the same size, incl. on mobile
    minWidth: "288px",
    maxWidth: "375px",
    p: 2,
    backgroundColor: "var(--colors-white)",
    borderRadius: 4,
    boxShadow: "var(--shadows-2)"
});

const favoriteButtonStyle = css({
    position: "absolute",
    top: 2,
    right: 2,
    zIndex: 30
});

const promotionBadgeStyle = css({
    position: "absolute",
    top: 2,
    left: 2,
    zIndex: 20
});

const promotionBadgeIconStyle = css({
    mr: 1
});

const linkStyle = css({
    display: "flex",
    flexDirection: "column",
    flex: "1 1 auto",
    cursor: "pointer",
    color: "inherit",
    _hover: {color: "inherit", textDecoration: "none"},
    _focus: {color: "inherit", textDecoration: "none"}
});

const planWrapStyle = css({
    flex: "0 0 auto",
    "& img": {
        objectFit: "contain",
        width: "100% !important",
        height: "100% !important"
    }
});

const infoListStyle = css({
    listStyle: "none",
    pl: 0,
    mt: 3,
    mb: 2,
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-evenly",
    alignItems: "center"
});

const infoItemStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center"
});

const labelStyle = css({
    color: "var(--colors-gray-700)",
    mb: 0.5
});
